import { useState } from "react";
import { Typography, Alert } from "@mui/material";
import { useAuthStore } from "@store/auth.store";
import { useChangePassword } from "@queries/auth/useChangePassword";
import { Form } from "@shared/components/Form";
import { TextField } from "@shared/components/TextField";
import { PasswordTextField } from "@shared/components/PasswordTextField";
import { SubmitButton } from "@avenue-8/sidekick-ui";
import { Container, FormItem } from "./styles";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

// Form validation schema
const securitySettingsSchema = yup.object({
  email: yup.string().required(),
  currentPassword: yup.string().required("Current password is required"),
  newPassword: yup.string().required("New password is required"),
  confirmPassword: yup
    .string()
    .required("Please confirm your new password")
    .oneOf([yup.ref("newPassword")], "Passwords do not match"),
});

type SecuritySettingsFormValues = {
  email: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export const SecuritySettings = () => {
  const user = useAuthStore((state) => state.user);
  const [success, setSuccess] = useState(false);

  // Initialize react-hook-form
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SecuritySettingsFormValues>({
    resolver: yupResolver(securitySettingsSchema),
    defaultValues: {
      email: user.email,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    reValidateMode: "onChange",
  });

  const changePassword = useChangePassword({
    onSuccess: () => {
      setSuccess(true);
      // Reset only password fields, keep email
      reset({
        email: user.email,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    },
    onError: () => {
      setSuccess(false);
    },
  });

  const onSubmit = (data: SecuritySettingsFormValues) => {
    setSuccess(false);

    changePassword.mutate({
      oldPassword: data.currentPassword,
      newPassword: data.newPassword,
    });
  };

  return (
    <Container size="medium">
      <Typography variant="h5" component="h2">
        Security Settings
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Your password has been changed successfully.
        </Alert>
      )}

      <Form
        onSubmit={handleSubmit(onSubmit)}
        aria-disabled={changePassword.isPending}
      >
        <FormItem size="medium">
          <TextField label="Email" {...register("email")} disabled fullWidth />
        </FormItem>

        <FormItem size="medium">
          <PasswordTextField
            label="Current Password"
            name="currentPassword"
            inputProps={{ ...register("currentPassword") }}
            fieldError={errors.currentPassword}
            fullWidth
          />
        </FormItem>

        <FormItem size="medium">
          <PasswordTextField
            label="New Password"
            name="newPassword"
            inputProps={{ ...register("newPassword") }}
            fieldError={errors.newPassword}
            fullWidth
          />
        </FormItem>

        <FormItem size="medium">
          <PasswordTextField
            label="Confirm New Password"
            name="confirmPassword"
            inputProps={{ ...register("confirmPassword") }}
            fieldError={errors.confirmPassword}
            fullWidth
          />
        </FormItem>

        <SubmitButton
          variant="contained"
          color="primary"
          isLoading={changePassword.isPending}
          loadingText="Changing password..."
          sx={{ mt: 2 }}
        >
          Change Password
        </SubmitButton>
      </Form>
    </Container>
  );
};
